import React, { useState, useEffect } from "react";
import {
  Video, Eye, CheckSquare, Square, Filter, Search,
  Crown, Shield, Unlock, Lock, Grid, List, Users, Save, RotateCcw
} from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useFeatureFlags, visibleTiers } from "../lib/featureFlags";
import ManageVideoModal from "../components/manage-video/ManageVideoModal";
import { useSearchParams } from "react-router-dom";

const NEON = "#ff2bd6";
const NEON_SOFT = "#ff4fd8";
const glow = "shadow-[0_0_14px_rgba(255,43,214,0.45)]";
const glowStrong = "shadow-[0_0_22px_rgba(255,43,214,0.75)]";
const textGlow = { textShadow: `0 0 10px ${NEON}99, 0 0 22px ${NEON}55` };

const panel = "bg-[#0d0617]/90 border border-[#ff2bd6]/40 rounded-xl " + glow;
const inputCls = "w-full bg-[#150a22] border border-[#ff2bd6]/30 rounded-lg px-4 py-2 text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-[#ff2bd6] focus:border-[#ff4fd8]";
const labelCls = "block text-sm text-pink-200/80 mb-2";
const btnBase = "px-4 py-2 rounded-lg text-sm font-semibold transition focus:outline-none focus:ring-2 focus:ring-[#ff4fd8] ";

// Tier palette stays distinct from the pink accent: Free=cyan, Basic=green, VIP=gold.
const TIERS = {
  free: {
    label: "Free", Icon: Unlock,
    on: "bg-cyan-500/25 text-cyan-100 border-cyan-300 shadow-[0_0_12px_rgba(34,211,238,0.55)]",
    off: "bg-[#150a22] text-zinc-400 border-zinc-700 hover:border-cyan-400",
    solid: "bg-cyan-600 hover:bg-cyan-500 text-white", outline: "border border-cyan-400/60 text-cyan-200 hover:bg-cyan-500/15",
    icon: "text-cyan-300",
  },
  basic: {
    label: "Basic", Icon: Shield,
    on: "bg-emerald-500/25 text-emerald-100 border-emerald-300 shadow-[0_0_12px_rgba(52,211,153,0.55)]",
    off: "bg-[#150a22] text-zinc-400 border-zinc-700 hover:border-emerald-400",
    solid: "bg-emerald-600 hover:bg-emerald-500 text-white", outline: "border border-emerald-400/60 text-emerald-200 hover:bg-emerald-500/15",
    icon: "text-emerald-300",
  },
  vip: {
    label: "VIP", Icon: Crown,
    on: "bg-amber-500/25 text-amber-100 border-amber-300 shadow-[0_0_12px_rgba(251,191,36,0.55)]",
    off: "bg-[#150a22] text-zinc-400 border-zinc-700 hover:border-amber-400",
    solid: "bg-amber-600 hover:bg-amber-500 text-white", outline: "border border-amber-400/60 text-amber-200 hover:bg-amber-500/15",
    icon: "text-amber-300",
  },
};

const muxThumb = (playbackId) =>
  `https://image.mux.com/${playbackId}/thumbnail.jpg?width=640&fit_mode=smartcrop`;

const CAST_VISIBILITY_GROUPS = [
  { label: "Profile", fields: [{ key: "photo", label: "Profile photo" }, { key: "bio", label: "Bio" }] },
  { label: "Details", fields: [
    { key: "height", label: "Height" }, { key: "weight", label: "Weight" },
    { key: "body_type", label: "Body type" }, { key: "ethnicity", label: "Ethnicity" },
    { key: "hair_color", label: "Hair color" }, { key: "eye_color", label: "Eye color" },
    { key: "tattoos", label: "Tattoos" }, { key: "piercings", label: "Piercings" }, { key: "position", label: "Position" },
    { key: "dick_size", label: "Dick size" }, { key: "tags", label: "Tags" },
  ] },
  { label: "Social links", fields: [
    { key: "instagram", label: "Instagram" }, { key: "twitter", label: "X" },
    { key: "onlyfans", label: "OnlyFans" }, { key: "link_in_bio", label: "Website" },
  ] },
];
const ALL_CAST_VISIBILITY_FIELDS = CAST_VISIBILITY_GROUPS.flatMap((group) => group.fields.map(({ key }) => key));

// Mux playback thumbnail -> stored thumbnail_url -> icon.
function VideoThumb({ video, className = "", iconSize = 40 }) {
  const playbackId = video.mux_playback_id || video.playback_id;
  const sources = [
    playbackId ? muxThumb(playbackId) : null,
    video.thumbnail_url || null,
  ].filter(Boolean);
  const [index, setIndex] = useState(0);

  useEffect(() => setIndex(0), [playbackId, video.thumbnail_url]);

  if (index >= sources.length) {
    return (
      <div className={`${className} bg-[#150a22] flex items-center justify-center`}>
        <Video size={iconSize} style={{ color: NEON_SOFT, opacity: 0.55 }} />
      </div>
    );
  }
  return (
    <img
      src={sources[index]}
      alt={video.title}
      loading="lazy"
      onError={() => setIndex(i => i + 1)}
      className={`${className} object-cover`}
    />
  );
}

export default function MemberAreasConfig() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("videos");
  const featureFlags = useFeatureFlags();
  const availableTiers = visibleTiers(featureFlags);
  const tierEntries = Object.entries(TIERS).filter(([key]) => availableTiers.some((tier) => tier.id === key));
  const [videos, setVideos] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // grid | list
  const [filterTier, setFilterTier] = useState("all"); // all | free | basic | vip | unassigned
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedVideos, setSelectedVideos] = useState(new Set());
  const [previewTier, setPreviewTier] = useState(null); // null | free | basic | vip
  const [previewVideos, setPreviewVideos] = useState([]);
  const [manageVideo, setManageVideo] = useState(null);
  const [castVisibleFields, setCastVisibleFields] = useState(ALL_CAST_VISIBILITY_FIELDS);
  const [savedCastVisibleFields, setSavedCastVisibleFields] = useState(ALL_CAST_VISIBILITY_FIELDS);
  const [castVisibilityLoading, setCastVisibilityLoading] = useState(true);
  const [savingCastVisibility, setSavingCastVisibility] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadCastVisibility();
  }, []);

  useEffect(() => {
    const requestedId = searchParams.get("videoId");
    if (!requestedId || !videos.length) return;
    const video = videos.find((item) => item.id === requestedId);
    if (video) {
      setManageVideo(video);
      setSearchParams((params) => { params.delete("videoId"); return params; }, { replace: true });
    }
  }, [videos, searchParams, setSearchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [videosRes, statsRes] = await Promise.all([
        api.get("/api/admin/member-areas/videos"),
        api.get("/api/admin/member-areas/stats")
      ]);

      setVideos(videosRes.data.videos || []);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load member areas data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadCastVisibility = async () => {
    setCastVisibilityLoading(true);
    try {
      const response = await api.get("/api/admin/member-areas/cast-visibility");
      const fields = Array.isArray(response.data.visible_fields)
        ? ALL_CAST_VISIBILITY_FIELDS.filter((field) => response.data.visible_fields.includes(field))
        : ALL_CAST_VISIBILITY_FIELDS;
      setCastVisibleFields(fields);
      setSavedCastVisibleFields(fields);
    } catch (error) {
      toast.error("Failed to load Cast & Crew visibility settings");
    } finally {
      setCastVisibilityLoading(false);
    }
  };

  const saveCastVisibility = async () => {
    setSavingCastVisibility(true);
    try {
      const response = await api.put("/api/admin/member-areas/cast-visibility", {
        visible_fields: castVisibleFields,
      });
      const fields = ALL_CAST_VISIBILITY_FIELDS.filter((field) => response.data.visible_fields.includes(field));
      setCastVisibleFields(fields);
      setSavedCastVisibleFields(fields);
      toast.success("Free Member Cast & Crew visibility saved");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to save Cast & Crew visibility settings");
    } finally {
      setSavingCastVisibility(false);
    }
  };

  const toggleCastVisibilityField = (field) => {
    setCastVisibleFields((current) => current.includes(field)
      ? current.filter((item) => item !== field)
      : ALL_CAST_VISIBILITY_FIELDS.filter((item) => item === field || current.includes(item)));
  };

  const toggleVideoTier = async (videoId, tier) => {
    try {
      const video = videos.find(v => v.id === videoId);
      const currentTiers = [];

      if (video.assigned_tiers.free) currentTiers.push("free");
      if (featureFlags.ENABLE_PAID_TIERS && video.assigned_tiers.basic) currentTiers.push("basic");
      if (featureFlags.ENABLE_PAID_TIERS && video.assigned_tiers.vip) currentTiers.push("vip");

      const newTiers = currentTiers.includes(tier)
        ? currentTiers.filter(t => t !== tier)
        : [...currentTiers, tier];

      await api.patch(`/api/admin/member-areas/video/${videoId}/tiers`, {
        video_id: videoId,
        tiers: newTiers
      });

      toast.success(`Updated tier assignment for ${video.title}`);
      loadData();
    } catch (error) {
      toast.error("Failed to update tier");
    }
  };

  const bulkAssignTiers = async (tiers) => {
    if (selectedVideos.size === 0) {
      toast.error("No videos selected");
      return;
    }

    try {
      await api.post("/api/admin/member-areas/bulk/tiers", {
        video_ids: Array.from(selectedVideos),
        tiers
      });

      toast.success(`Assigned ${tiers.join(", ")} tier(s) to ${selectedVideos.size} video(s)`);
      setSelectedVideos(new Set());
      loadData();
    } catch (error) {
      toast.error("Failed to bulk assign tiers");
    }
  };

  const bulkRemoveTiers = async (tiers) => {
    if (selectedVideos.size === 0) {
      toast.error("No videos selected");
      return;
    }

    const count = selectedVideos.size;
    const tierText = tiers.length === availableTiers.length ? "ALL visible tiers" : `the ${TIERS[tiers[0]].label} tier`;
    if (!window.confirm(`Remove ${count} selected video(s) from ${tierText}?`)) return;

    try {
      await api.post("/api/admin/member-areas/bulk/unassign", {
        video_ids: Array.from(selectedVideos),
        tiers
      });

      await loadData();
      setSelectedVideos(new Set());
      toast.success(`Removed ${count} video(s) from ${tierText}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove tiers");
    }
  };

  const loadPreview = async (tier) => {
    if (tier !== "free" && !featureFlags.ENABLE_PAID_TIERS) return;
    try {
      setPreviewTier(tier);
      const res = await api.get(`/api/admin/member-areas/preview/${tier}`);
      setPreviewVideos(res.data.videos || []);
    } catch (error) {
      toast.error(`Failed to load ${tier} tier preview`);
    }
  };

  const toggleSelectVideo = (videoId) => {
    const newSelected = new Set(selectedVideos);
    if (newSelected.has(videoId)) {
      newSelected.delete(videoId);
    } else {
      newSelected.add(videoId);
    }
    setSelectedVideos(newSelected);
  };

  const filteredVideos = videos.filter(video => {
    const needle = searchTerm.toLowerCase();
    const matchesSearch = (video.title || "").toLowerCase().includes(needle) ||
                         (video.cast || "").toLowerCase().includes(needle);

    if (!matchesSearch) return false;

    if (filterTier === "all") return true;
    if (filterTier === "unassigned") {
      return !video.assigned_tiers.free && (!featureFlags.ENABLE_PAID_TIERS || (!video.assigned_tiers.basic && !video.assigned_tiers.vip));
    }

    return video.assigned_tiers[filterTier];
  });

  const allVisibleSelected =
    filteredVideos.length > 0 && filteredVideos.every(v => selectedVideos.has(v.id));

  const toggleSelectAll = () => {
    const next = new Set(selectedVideos);
    if (allVisibleSelected) {
      filteredVideos.forEach(v => next.delete(v.id));
    } else {
      filteredVideos.forEach(v => next.add(v.id));
    }
    setSelectedVideos(next);
  };

  const shell = (children) => (
    <div className="p-4 md:p-6">
      <div
        className="max-w-7xl mx-auto rounded-2xl p-6 border border-[#ff2bd6]/30"
        style={{ background: "linear-gradient(160deg, #050109 0%, #0d0420 55%, #150629 100%)" }}
      >
        {children}
      </div>
    </div>
  );

  if (loading) {
    return shell(
      <div className="flex items-center justify-center h-64">
        <div className="text-pink-200" style={textGlow}>Loading member areas...</div>
      </div>
    );
  }

  if (previewTier) {
    return shell(
      <>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-2" style={textGlow}>
              Preview: {previewTier.toUpperCase()} Tier
            </h2>
            <p className="text-pink-100/70">
              Showing exactly what {previewTier} members see ({previewVideos.length} videos)
            </p>
          </div>
          <button
            onClick={() => setPreviewTier(null)}
            className={`${btnBase} border border-[#ff2bd6] text-white hover:bg-[#ff2bd6]/20 ${glow}`}
          >
            Exit Preview
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {previewVideos.map((video) => (
            <div key={video.id} className={`${panel} overflow-hidden`}>
              <VideoThumb video={video} className="w-full aspect-video" />
              <div className="p-4">
                <h3 className="font-semibold text-white mb-1">{video.title}</h3>
                <p className="text-sm text-pink-100/70">{video.cast || "No cast info"}</p>
              </div>
            </div>
          ))}

          {previewVideos.length === 0 && (
            <div className="col-span-full text-center py-12 text-pink-100/70">
              No videos assigned to {previewTier} tier
            </div>
          )}
        </div>
      </>
    );
  }

  const statTiles = stats && [
    { label: "Total Videos", value: stats.total, Icon: Video, color: "text-pink-300" },
    { label: "Free Tier", value: stats.free, Icon: Unlock, color: TIERS.free.icon },
    ...(featureFlags.ENABLE_PAID_TIERS ? [
      { label: "Basic Tier", value: stats.basic, Icon: Shield, color: TIERS.basic.icon },
      { label: "VIP Tier", value: stats.vip, Icon: Crown, color: TIERS.vip.icon },
    ] : []),
    { label: "Unassigned", value: stats.unassigned, Icon: Lock, color: "text-red-300" },
  ];

  return shell(
    <>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2" style={textGlow}>Member Areas</h1>
          <p className="text-pink-100/70">Assign videos to member tiers (Free/Basic/VIP)</p>
        </div>
        <Video size={40} style={{ color: NEON, filter: `drop-shadow(0 0 8px ${NEON})` }} />
      </div>

      <div role="tablist" aria-label="Member Areas settings" className="mb-6 flex flex-wrap gap-2 border-b border-[#ff2bd6]/25 pb-3">
        <button type="button" role="tab" aria-selected={activeTab === "videos"} onClick={() => setActiveTab("videos")} className={`${btnBase} ${activeTab === "videos" ? "bg-[#ff2bd6] text-white" : "border border-[#ff2bd6]/30 bg-[#150a22] text-pink-100/80"}`}>
          <Video size={15} className="mr-2 inline" />Videos &amp; tiers
        </button>
        <button type="button" role="tab" aria-selected={activeTab === "cast_crew"} onClick={() => setActiveTab("cast_crew")} className={`${btnBase} ${activeTab === "cast_crew" ? "bg-[#ff2bd6] text-white" : "border border-[#ff2bd6]/30 bg-[#150a22] text-pink-100/80"}`}>
          <Users size={15} className="mr-2 inline" />Cast &amp; Crew
        </button>
      </div>

      {activeTab === "cast_crew" ? (
        <section aria-label="Free Member Cast & Crew visibility" className={`${panel} p-5 md:p-6`}>
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#ff2bd6]/20 pb-4">
            <div>
              <h2 className="text-lg font-semibold text-white">Free Member Cast &amp; Crew</h2>
              <p className="mt-1 max-w-2xl text-sm text-pink-100/70">Choose which CRM contact-card details appear on free-member video previews. Name and role always remain visible.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => setCastVisibleFields(ALL_CAST_VISIBILITY_FIELDS)} disabled={castVisibilityLoading || savingCastVisibility} className={`${btnBase} border border-[#ff2bd6]/40 bg-[#150a22] text-pink-100 hover:bg-[#ff2bd6]/15 disabled:opacity-50`}>
                <RotateCcw size={14} className="mr-1.5 inline" />Show all
              </button>
              <button type="button" onClick={saveCastVisibility} disabled={castVisibilityLoading || savingCastVisibility || JSON.stringify(castVisibleFields) === JSON.stringify(savedCastVisibleFields)} className={`${btnBase} bg-[#ff2bd6] text-white hover:bg-[#ff4fd8] disabled:opacity-50`}>
                <Save size={14} className="mr-1.5 inline" />{savingCastVisibility ? "Saving…" : "Save visibility"}
              </button>
            </div>
          </div>

          {castVisibilityLoading ? (
            <p className="py-8 text-sm text-pink-100/70">Loading visibility settings…</p>
          ) : (
            <div className="mt-5 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {CAST_VISIBILITY_GROUPS.map((group) => (
                <fieldset key={group.label} className="rounded-lg border border-[#ff2bd6]/20 bg-[#09040f]/70 p-4">
                  <legend className="px-1 text-sm font-semibold text-pink-100">{group.label}</legend>
                  <div className="mt-2 space-y-3">
                    {group.fields.map(({ key, label }) => (
                      <label key={key} className="flex cursor-pointer items-center gap-3 text-sm text-zinc-200">
                        <input type="checkbox" checked={castVisibleFields.includes(key)} onChange={() => toggleCastVisibilityField(key)} className="h-4 w-4 accent-[#ff2bd6]" />
                        {label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </div>
          )}
        </section>
      ) : (
        <>

      {/* Stats Cards */}
      {statTiles && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
          {statTiles.map(({ label, value, Icon, color }) => (
            <div key={label} className={`${panel} p-6`}>
              <div className="flex items-center gap-3 mb-2">
                <Icon size={20} className={color} />
                <span className="text-sm text-pink-100/80">{label}</span>
              </div>
              <div className="text-3xl font-bold text-white" style={textGlow}>{value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Preview Buttons */}
      <div className={`${panel} p-6 mb-6`}>
        <h3 className="text-white font-semibold mb-4">Preview Member Experience</h3>
        <div className="flex flex-wrap gap-3">
          {tierEntries.map(([key, t]) => (
            <button
              key={key}
              onClick={() => loadPreview(key)}
              className={`${btnBase} flex items-center gap-2 ${t.solid}`}
            >
              <Eye size={16} />
              Preview {t.label} Tier
            </button>
          ))}
        </div>
      </div>

      {/* Filters & Bulk Actions */}
      <div className={`${panel} p-6 mb-6`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label className={labelCls}>
              <Search size={14} className="inline mr-1" />
              Search
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search videos..."
              className={inputCls}
            />
          </div>

          <div>
            <label className={labelCls}>
              <Filter size={14} className="inline mr-1" />
              Filter by Tier
            </label>
            <select
              value={filterTier}
              onChange={(e) => setFilterTier(e.target.value)}
              className={inputCls}
            >
              <option value="all">All Videos</option>
              <option value="free">Free Tier Only</option>
              {featureFlags.ENABLE_PAID_TIERS && <option value="basic">Basic Tier Only</option>}
              {featureFlags.ENABLE_PAID_TIERS && <option value="vip">VIP Tier Only</option>}
              <option value="unassigned">Unassigned</option>
            </select>
          </div>

          <div>
            <label className={labelCls}>View Mode</label>
            <div className="flex gap-2">
              {[["grid", Grid, "Grid"], ["list", List, "List"]].map(([mode, Icon, text]) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`${btnBase} flex-1 ${
                    viewMode === mode
                      ? `bg-[#ff2bd6] text-white ${glowStrong}`
                      : "bg-[#150a22] text-pink-100/80 border border-[#ff2bd6]/30 hover:border-[#ff4fd8]"
                  }`}
                >
                  <Icon size={16} className="inline mr-2" />
                  {text}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Selection bar */}
        <div className="flex flex-wrap items-center gap-3 pt-4 border-t border-[#ff2bd6]/25">
          <label className="flex items-center gap-2 text-sm text-pink-100 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              onChange={toggleSelectAll}
              disabled={filteredVideos.length === 0}
              className="w-4 h-4 accent-[#ff2bd6] focus:ring-2 focus:ring-[#ff4fd8]"
              data-testid="select-all-visible"
            />
            Select all visible ({filteredVideos.length})
          </label>

          {selectedVideos.size > 0 && (
            <>
              <span className="text-sm text-pink-200" style={textGlow}>
                {selectedVideos.size} video(s) selected
              </span>

              <div className="flex flex-wrap items-center gap-2 w-full">
                {tierEntries.map(([key, t]) => (
                  <button key={`a-${key}`} onClick={() => bulkAssignTiers([key])} className={`${btnBase} ${t.solid}`}>
                    Assign to {t.label}
                  </button>
                ))}
                <button
                  onClick={() => bulkAssignTiers(availableTiers.map((tier) => tier.id))}
                  className={`${btnBase} bg-[#ff2bd6] hover:bg-[#ff4fd8] text-white ${glow}`}
                >
                  Assign to All Tiers
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 w-full">
                {tierEntries.map(([key, t]) => (
                  <button key={`r-${key}`} onClick={() => bulkRemoveTiers([key])} className={`${btnBase} bg-transparent ${t.outline}`}>
                    Remove from {t.label}
                  </button>
                ))}
                <button
                  onClick={() => bulkRemoveTiers(availableTiers.map((tier) => tier.id))}
                  className={`${btnBase} bg-transparent border border-red-400/70 text-red-200 hover:bg-red-500/15`}
                >
                  Remove from All Tiers
                </button>

                <button
                  onClick={() => setSelectedVideos(new Set())}
                  className={`${btnBase} ml-auto bg-[#150a22] border border-[#ff2bd6]/40 text-white hover:bg-[#ff2bd6]/20`}
                >
                  Clear Selection
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Videos Grid/List */}
      {viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVideos.map((video) => {
            const isSelected = selectedVideos.has(video.id);

            return (
              <div
                key={video.id}
                className={`bg-[#0d0617]/90 border rounded-xl overflow-hidden transition ${
                  isSelected
                    ? `border-[#ff4fd8] ring-2 ring-[#ff2bd6] ${glowStrong}`
                    : "border-[#ff2bd6]/35 hover:border-[#ff4fd8] " + glow
                }`}
              >
                <div className="relative">
                  <VideoThumb video={video} className="w-full aspect-video" />

                  <button
                    onClick={() => toggleSelectVideo(video.id)}
                    aria-label={isSelected ? "Deselect video" : "Select video"}
                    className="absolute top-2 left-2 p-2 bg-black/60 rounded-lg hover:bg-black/80 transition focus:outline-none focus:ring-2 focus:ring-[#ff4fd8]"
                  >
                    {isSelected ? (
                      <CheckSquare size={20} style={{ color: NEON_SOFT }} />
                    ) : (
                      <Square size={20} className="text-white" />
                    )}
                  </button>
                </div>

                <div className="p-4">
                  <h3 className="font-semibold text-white mb-1 truncate">
                    {video.title}
                  </h3>
                  <p className="text-sm text-pink-100/70 mb-3 truncate">
                    {video.cast || "No cast info"}
                  </p>

                  <div className="flex gap-2">
                    {tierEntries.map(([key, t]) => (
                      <button
                        key={key}
                        onClick={() => toggleVideoTier(video.id, key)}
                        className={`flex-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition border focus:outline-none focus:ring-2 focus:ring-[#ff4fd8] ${
                          video.assigned_tiers[key] ? t.on : t.off
                        }`}
                      >
                        <t.Icon size={12} className="inline mr-1" />
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setManageVideo(video)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-cyan-500/40 bg-cyan-900/20 text-cyan-200 hover:bg-cyan-900/40 text-xs font-semibold">
                    <Video size={14} /> Manage Video
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className={`${panel} overflow-hidden`}>
          <table className="w-full">
            <thead className="bg-[#1a0b2e]">
              <tr>
                <th className="px-6 py-4 text-left">
                  <button
                    onClick={toggleSelectAll}
                    aria-label="Select all visible"
                    className="text-pink-200 hover:text-white transition"
                  >
                    {allVisibleSelected ? <CheckSquare size={18} style={{ color: NEON_SOFT }} /> : <Square size={18} />}
                  </button>
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-pink-200 uppercase">Video</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-cyan-200 uppercase">Free</th>
                {featureFlags.ENABLE_PAID_TIERS && <th className="px-6 py-4 text-left text-xs font-semibold text-emerald-200 uppercase">Basic</th>}
                {featureFlags.ENABLE_PAID_TIERS && <th className="px-6 py-4 text-left text-xs font-semibold text-amber-200 uppercase">VIP</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-[#ff2bd6]/15">
              {filteredVideos.map((video) => {
                const isSelected = selectedVideos.has(video.id);
                return (
                  <tr
                    key={video.id}
                    className={`transition ${isSelected ? "bg-[#ff2bd6]/15 shadow-[inset_3px_0_0_#ff2bd6]" : "hover:bg-[#ff2bd6]/10"}`}
                  >
                    <td className="px-6 py-4">
                      <button
                        onClick={() => toggleSelectVideo(video.id)}
                        aria-label={isSelected ? "Deselect video" : "Select video"}
                        className="text-pink-200 hover:text-white transition"
                      >
                        {isSelected ? <CheckSquare size={18} style={{ color: NEON_SOFT }} /> : <Square size={18} />}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <VideoThumb video={video} className="w-24 aspect-video rounded shrink-0" iconSize={18} />
                        <div>
                          <div className="font-semibold text-white">{video.title}</div>
                          <div className="text-sm text-pink-100/70">{video.cast || "No cast"}</div>
                        </div>
                      </div>
                    </td>
                    {tierEntries.map(([key, t]) => (
                      <td key={key} className="px-6 py-4">
                        <button
                          onClick={() => toggleVideoTier(video.id, key)}
                          aria-label={`Toggle ${t.label}`}
                          className={`p-2 rounded-lg border transition focus:outline-none focus:ring-2 focus:ring-[#ff4fd8] ${
                            video.assigned_tiers[key] ? t.on : t.off
                          }`}
                        >
                          <t.Icon size={16} />
                        </button>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filteredVideos.length === 0 && (
            <div className="text-center py-12 text-pink-100/70">
              No videos found
            </div>
          )}
        </div>
      )}

      {filteredVideos.length === 0 && videos.length > 0 && (
        <div className="text-center py-12 text-pink-100/70">
          No videos match your filters
        </div>
      )}
      {manageVideo && (
        <ManageVideoModal
          video={manageVideo}
          source="member_areas"
          onClose={() => setManageVideo(null)}
          onChange={(updated) => {
            setManageVideo((current) => current && current.id === updated?.id ? { ...current, ...updated } : current);
            setVideos((current) => current.map((item) => item.id === updated?.id ? { ...item, ...updated } : item));
          }}
        />
      )}
        </>
      )}
    </>
  );
}
